import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { PasswordHintTooltip } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useSetPassword } from "@/hooks/mutations";

const acceptInvitationSetPasswordFormSchema = z
  .object({
    password: z
      .string()
      .min(1, "Required")
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Za-z]/, "Password must contain at least one letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(/[^A-Za-z0-9]/, "Password must contain at least one symbol"),
    confirmPassword: z.string().min(1, "Required"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords must match",
    path: ["confirmPassword"],
  });

export const AcceptInvitationSetPasswordForm = ({
  onSuccess,
}: {
  onSuccess: () => void;
}) => {
  const { mutate: setPassword } = useSetPassword();

  const form = useForm<z.infer<typeof acceptInvitationSetPasswordFormSchema>>({
    resolver: zodResolver(acceptInvitationSetPasswordFormSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = (
    values: z.infer<typeof acceptInvitationSetPasswordFormSchema>,
  ) => {
    setPassword(
      {
        setPasswordRequest: {
          password: values.password,
          confirmPassword: values.confirmPassword,
        },
      },
      {
        onSuccess: () => {
          onSuccess();
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-6"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <div className="flex items-center gap-2">
                  <FormLabel>Password</FormLabel>
                  <PasswordHintTooltip />
                </div>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Password (8 or more characters)"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Confirm Password</FormLabel>
                <FormControl>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button type="submit">Confirm</Button>
      </form>
    </Form>
  );
};
